import { computed, inject, Injectable } from '@angular/core';
import {
  RequestWorkflowWithNodes,
  WorkflowFormSubmissionExternal,
  WorkflowNodeExternal,
  WorkflowNodeFormWithPopulatedFieldsExternal,
} from '@workflows/types';
import { combineLatest, map, Observable } from 'rxjs';
import { WorkflowDataService } from './';

/**
 * Сервис для выборки данных workflow (read-only операции)
 * Использует паттерн Selector для получения производных данных
 * Отвечает за получение информации о формах, нодах, статусах
 */
@Injectable({
  providedIn: 'root',
})
export class WorkflowSelectorService {
  private readonly DEFAULT_WORKFLOW_NAME = 'Без названия';

  private readonly dataService = inject(WorkflowDataService);

  /**
   * Helper: Найти элемент с минимальным order
   */
  findMinByOrder<T extends { order?: number }>(items: T[]): T | null {
    if (!items.length) return null;

    return items.reduce((min, item) =>
      (item.order ?? Infinity) < (min.order ?? Infinity) ? item : min,
    );
  }

  /**
   * Helper: Найти элемент с максимальным order
   */
  findMaxByOrder<T extends { order?: number }>(items: T[]): T | null {
    if (!items.length) return null;

    return items.reduce((max, item) =>
      (item.order ?? -Infinity) > (max.order ?? -Infinity) ? item : max,
    );
  }

  /**
   * Формы ноды без повторов: при двух регламентах одной версии сервер отдаёт
   * одну и ту же форму для каждого экземпляра
   */
  getNodeForms(nodeId: number): WorkflowNodeFormWithPopulatedFieldsExternal[] {
    return uniqueById(
      this.dataService
        .$requestsWorkflowData()
        ?.workflowNodesForms.filter(form => form.workflowNodeId === nodeId) ??
        [],
    ).sort((a, b) => a.order - b.order);
  }

  /**
   * Получить первую форму ноды
   */
  getFirstFormOfNode(nodeId: number): number | null {
    const nodeForms = this.getNodeForms(nodeId);

    return nodeForms[0]?.id ?? null;
  }

  /**
   * Получить последнюю форму ноды
   */
  getLastFormOfNode(nodeId: number): number | null {
    const nodeForms = this.getNodeForms(nodeId);

    return nodeForms[nodeForms.length - 1]?.id ?? null;
  }

  /**
   * Получить первую ноду workflow
   */
  getFirstNodeOfWorkflow(workflowId: string): WorkflowNodeExternal | null {
    const workflow = this.dataService
      .$requestsWorkflowData()
      ?.requestsWorkflows.find(wf => wf.id === workflowId);

    return workflow ? this.findMinByOrder(workflow.nodes) : null;
  }

  /**
   * Получить последнюю ноду workflow
   */
  getLastNodeOfWorkflow(workflowId: string): WorkflowNodeExternal | null {
    const workflow = this.dataService
      .$requestsWorkflowData()
      ?.requestsWorkflows.find(wf => wf.id === workflowId);

    return workflow ? this.findMaxByOrder(workflow.nodes) : null;
  }

  /**
   * Ноды регламента в порядке прохождения: копия, отсортированная по (order, id).
   * Не полагаемся на порядок массива из API и на непрерывность order (1,2,3)
   */
  getSortedNodes(
    workflow: RequestWorkflowWithNodes | null,
  ): WorkflowNodeExternal[] {
    return [...(workflow?.nodes ?? [])].sort(
      (a, b) => a.order - b.order || a.id - b.id,
    );
  }

  /**
   * Предыдущая нода регламента или null, если нода первая
   */
  getPreviousNode(
    nodeId: number,
    workflow: RequestWorkflowWithNodes | null,
  ): WorkflowNodeExternal | null {
    const nodes = this.getSortedNodes(workflow);
    const index = nodes.findIndex(node => node.id === nodeId);

    return index > 0 ? nodes[index - 1] : null;
  }

  /**
   * Получить ноду по порядковому номеру
   */
  getNodeByOrder(
    order: number,
    workflow: RequestWorkflowWithNodes | null,
  ): WorkflowNodeExternal | null {
    return (
      workflow?.nodes.find((n: WorkflowNodeExternal) => n.order === order) ??
      null
    );
  }

  /**
   * Нода формы (ноды и формы принадлежат версии регламента).
   * null — форма отсутствует в текущей схеме (устаревшая ссылка)
   */
  getNodeIdByFormId(formId: number): number | null {
    const wfdata = this.dataService.$requestsWorkflowData();

    return (
      wfdata?.workflowNodesForms.find(form => form.id === formId)
        ?.workflowNodeId ?? null
    );
  }

  /**
   * Получить следующий workflow
   */
  getNextWorkflow(id: string): RequestWorkflowWithNodes | null {
    const workflows =
      this.dataService.$requestsWorkflowData()?.requestsWorkflows;

    if (!workflows) return null;

    const currentIndex = workflows.findIndex(wf => wf.id === id);

    if (currentIndex === -1 || currentIndex === workflows.length - 1) {
      return null;
    }

    return workflows[currentIndex + 1];
  }

  /**
   * Получить предыдущий workflow
   */
  getPreviousWorkflow(id: string): RequestWorkflowWithNodes | null {
    const workflows =
      this.dataService.$requestsWorkflowData()?.requestsWorkflows;

    if (!workflows) return null;

    const currentIndex = workflows.findIndex(wf => wf.id === id);

    if (currentIndex <= 0) {
      return null;
    }

    return workflows[currentIndex - 1];
  }

  /**
   * Получить последний workflow
   */
  getLastWorkflow(): RequestWorkflowWithNodes | null {
    const workflows =
      this.dataService.$requestsWorkflowData()?.requestsWorkflows;

    if (!workflows || workflows.length === 0) return null;

    return workflows[workflows.length - 1];
  }

  /**
   * Получить следующую форму в ноде
   */
  getNextFormInNode(nodeId: number, currentFormId: number): number | null {
    const nodeForms = this.getNodeForms(nodeId);
    const currentFormIndex = nodeForms.findIndex(
      form => form.id === currentFormId,
    );

    return currentFormIndex !== -1 && currentFormIndex < nodeForms.length - 1
      ? nodeForms[currentFormIndex + 1].id
      : null;
  }

  /**
   * Получить следующую форму в workflow (включая переход к следующей ноде)
   */
  getNextFormInWorkflow(
    workflowId: string,
    currentNodeId: number,
    currentFormId: number,
  ): number | null {
    const workflow = this.dataService
      .$requestsWorkflowData()
      ?.requestsWorkflows.find(wf => wf.id === workflowId);

    if (!workflow) return null;

    const sortedNodes = this.getSortedNodes(workflow);
    const currentNodeIndex = sortedNodes.findIndex(
      node => node.id === currentNodeId,
    );

    if (currentNodeIndex === -1) return null;

    // Пытаемся найти следующую форму в текущей ноде
    const nextFormId = this.getNextFormInNode(currentNodeId, currentFormId);

    if (nextFormId !== null) {
      return nextFormId;
    }

    // Если форм нет, переходим к следующей ноде
    const nextNode = sortedNodes[currentNodeIndex + 1];

    if (nextNode) {
      const nextFormId = this.getFirstFormOfNode(nextNode.id);

      if (nextFormId !== null) {
        return nextFormId;
      }
    }

    return null;
  }

  /**
   * Получить имя workflow
   */
  getWorkFlowName(requestWorkflowId: string): string {
    const requestWorkflow = this.dataService
      .$requestsWorkflowData()
      ?.requestsWorkflows.find(rwf => rwf.id === requestWorkflowId);

    return (
      requestWorkflow?.workflowVersion.workflow.name ??
      this.DEFAULT_WORKFLOW_NAME
    );
  }

  getWorkflowNodeForms(
    workflowNodeId: number,
  ): Observable<WorkflowNodeFormWithPopulatedFieldsExternal[]> {
    return this.dataService.$requestsWorkflowDataObservable$.pipe(
      map(wfData =>
        uniqueById(
          wfData?.workflowNodesForms.filter(
            (form: WorkflowNodeFormWithPopulatedFieldsExternal) =>
              form.workflowNodeId === workflowNodeId,
          ) ?? [],
        ),
      ),
    );
  }

  /**
   * Первая форма первого регламента заявки (позиция в списке, а не поиск владельца формы)
   */
  isFirstForm(requestWorkflowId: string, formID: string): boolean {
    const wfData = this.dataService.$requestsWorkflowData();

    if (!wfData) return false;

    const firstWorkflow = wfData.requestsWorkflows[0];

    if (!firstWorkflow || firstWorkflow.id !== requestWorkflowId) return false;

    const firstNode = this.findMinByOrder(firstWorkflow.nodes);

    if (!firstNode) return false;

    return this.getNodeForms(firstNode.id)[0]?.id.toString() === formID;
  }

  /**
   * Проверить, является ли форма типа inputPhotos
   */
  isInputPhotosForm(
    formId: WorkflowNodeFormWithPopulatedFieldsExternal['id'],
  ): boolean {
    const form = this.dataService
      .$requestsWorkflowData()
      ?.workflowNodesForms.find(f => f.id === formId);

    if (!form) return false;

    return form.schema.groups.some(group =>
      group.fields.some(field => field.type === 'inputPhotos'),
    );
  }

  // --------------------------------------------------------------------------
  // Счётчики и проверки статусов
  // --------------------------------------------------------------------------

  /**
   * Количество валидных форм ноды в экземпляре регламента заявки
   */
  getNodeValidFormsCount$(
    nodeId: number,
    requestWorkflowId: string,
  ): Observable<number> {
    return this.dataService.$requestsWorkflowDataObservable$.pipe(
      map(wfData => {
        if (!wfData) return 0;

        const formIds = getNodeFormIds(wfData.workflowNodesForms, nodeId);

        return getInstanceSubmissions(
          wfData.submissions,
          requestWorkflowId,
        ).filter(sb => formIds.has(sb.workflowNodeFormId) && sb.isValid).length;
      }),
    );
  }

  /**
   * Общее количество форм ноды (одинаково для всех экземпляров версии)
   */
  getWorkflowNodeFormsCount$(nodeId: number): Observable<number> {
    return this.dataService.$requestsWorkflowDataObservable$.pipe(
      map(wfData =>
        wfData ? getNodeFormIds(wfData.workflowNodesForms, nodeId).size : 0,
      ),
    );
  }

  /**
   * Все формы ноды экземпляра валидны
   */
  allNodeFormsCompleted$(
    nodeId: number,
    requestWorkflowId: string,
  ): Observable<boolean> {
    return combineLatest({
      valid: this.getNodeValidFormsCount$(nodeId, requestWorkflowId),
      total: this.getWorkflowNodeFormsCount$(nodeId),
    }).pipe(map(({ valid, total }) => total > 0 && valid === total));
  }

  /**
   * В ноде экземпляра есть невалидные формы
   */
  someNodeFormsAreInvalid$(
    nodeId: number,
    requestWorkflowId: string,
  ): Observable<boolean> {
    return this.dataService.$requestsWorkflowDataObservable$.pipe(
      map(wfData => {
        if (!wfData) return false;

        const nodeFormIds = getNodeFormIds(wfData.workflowNodesForms, nodeId);

        return getInstanceSubmissions(
          wfData.submissions,
          requestWorkflowId,
        ).some(
          submission =>
            !submission.isValid &&
            nodeFormIds.has(submission.workflowNodeFormId),
        );
      }),
    );
  }

  /**
   * У ноды экземпляра есть submissions
   */
  nodeHasSubmissions$(
    nodeId: number,
    requestWorkflowId: string,
  ): Observable<boolean> {
    return this.dataService.$requestsWorkflowDataObservable$.pipe(
      map(wfData => {
        if (!wfData) return false;

        const nodeFormIds = getNodeFormIds(wfData.workflowNodesForms, nodeId);

        return getInstanceSubmissions(
          wfData.submissions,
          requestWorkflowId,
        ).some(submission => nodeFormIds.has(submission.workflowNodeFormId));
      }),
    );
  }

  /**
   * Проверить, в процессе ли нода (имеет последнюю посещённую форму)
   */
  nodeIsInProgress$(nodeId: number): Observable<boolean> {
    return this.dataService.$requestsWorkflowDataObservable$.pipe(
      map(wfData => {
        if (!wfData?.requestWorkflowsContext.lastVisitedFormId) return false;

        return getNodeFormIds(wfData.workflowNodesForms, nodeId).has(
          wfData.requestWorkflowsContext.lastVisitedFormId,
        );
      }),
    );
  }

  /**
   * Форма экземпляра невалидна
   */
  formIsInvalid$(
    formId: number,
    requestWorkflowId: string,
  ): Observable<boolean> {
    return this.dataService.$requestsWorkflowDataObservable$.pipe(
      map(
        wfData =>
          !!wfData &&
          getInstanceSubmissions(wfData.submissions, requestWorkflowId).some(
            sb => sb.workflowNodeFormId === formId && !sb.isValid,
          ),
      ),
    );
  }

  /**
   * Форма экземпляра отправлена (регламент заявки завершён)
   */
  formIsSubmitted$(
    formId: number,
    requestWorkflowId: string,
  ): Observable<boolean> {
    return this.dataService.$requestsWorkflowDataObservable$.pipe(
      map(wfData => {
        if (!wfData) return false;

        const requestWorkflow = wfData.requestsWorkflows.find(
          rw => rw.id === requestWorkflowId,
        );

        return (
          requestWorkflow?.state === 'completed' &&
          getInstanceSubmissions(wfData.submissions, requestWorkflowId).some(
            sb => sb.workflowNodeFormId === formId,
          )
        );
      }),
    );
  }

  /**
   * Все ноды экземпляра регламента заполнены валидно
   */
  allWorkflowNodesCompleted$(requestWorkflowId: string): boolean {
    const wfData = this.dataService.$requestsWorkflowData();

    if (!wfData) return false;

    const requestWorkflow = wfData.requestsWorkflows.find(
      rwf => rwf.id === requestWorkflowId,
    );

    return (
      !!requestWorkflow &&
      isRequestWorkflowFilled(
        requestWorkflow,
        wfData.workflowNodesForms,
        wfData.submissions,
      )
    );
  }

  /**
   * Проверить, завершены ли все workflows
   */
  areWorkflowsCompleted(): boolean {
    const wfData = this.dataService.$requestsWorkflowData();

    if (!wfData) return false;

    return wfData.requestWorkflowsContext.workflowsCompleted;
  }

  /**
   * Каждый регламент заявки заполнен валидно (проверка по каждому экземпляру отдельно)
   */
  areAllWorkflowNodesCompleted(): boolean {
    const wfData = this.dataService.$requestsWorkflowData();

    if (!wfData?.requestsWorkflows.length) return false;

    return wfData.requestsWorkflows.every(requestWorkflow =>
      isRequestWorkflowFilled(
        requestWorkflow,
        wfData.workflowNodesForms,
        wfData.submissions,
      ),
    );
  }

  /**
   * Helper: Проверить, что requestWorkflows существуют и не пустые
   */
  requestWorkflowsExist = computed(() => {
    const wfData = this.dataService.$requestsWorkflowData();

    return !!wfData?.requestsWorkflows?.length;
  });
}

function uniqueById<T extends { id: number }>(items: T[]): T[] {
  const seen = new Set<number>();

  return items.filter(item => {
    if (seen.has(item.id)) {
      return false;
    }

    seen.add(item.id);

    return true;
  });
}

function getNodeFormIds(
  forms: WorkflowNodeFormWithPopulatedFieldsExternal[],
  nodeId: number,
): Set<number> {
  return new Set(
    forms.filter(form => form.workflowNodeId === nodeId).map(form => form.id),
  );
}

/**
 * Submissions одного экземпляра регламента заявки
 */
function getInstanceSubmissions(
  submissions: WorkflowFormSubmissionExternal[],
  requestWorkflowId: string,
): WorkflowFormSubmissionExternal[] {
  return submissions.filter(
    submission => submission.requestWorkflowId === requestWorkflowId,
  );
}

/**
 * Все ноды экземпляра имеют валидные submissions этого экземпляра для каждой формы
 */
function isRequestWorkflowFilled(
  requestWorkflow: RequestWorkflowWithNodes,
  forms: WorkflowNodeFormWithPopulatedFieldsExternal[],
  submissions: WorkflowFormSubmissionExternal[],
): boolean {
  if (!requestWorkflow.nodes.length) return false;

  const instanceSubmissions = getInstanceSubmissions(
    submissions,
    requestWorkflow.id,
  );

  return requestWorkflow.nodes.every(node => {
    const nodeFormIds = getNodeFormIds(forms, node.id);

    if (!nodeFormIds.size) return false;

    const validFormIds = new Set(
      instanceSubmissions
        .filter(sb => nodeFormIds.has(sb.workflowNodeFormId) && sb.isValid)
        .map(sb => sb.workflowNodeFormId),
    );

    return validFormIds.size === nodeFormIds.size;
  });
}
